/** Graph helpers ported from agent_platform_sdk.render (kept output-compatible; see layout.test). */
import type { AgentSpec } from "./types";

export type FlowEdgeTuple = [source: string, target: string, when: string | null];

export function specEdges(spec: AgentSpec): FlowEdgeTuple[] {
  const g = spec.spec.graph;
  const out: FlowEdgeTuple[] = [];
  for (const e of g.edges ?? []) out.push([e.from, e.to, e.when ?? null]);
  for (const n of g.nodes) {
    if (n.type === "router") {
      for (const r of n.routes ?? []) out.push([n.id, r.to, r.when]);
      if (n.default) out.push([n.id, n.default, "default"]);
    } else if (n.type === "parallel") {
      for (const b of n.branches ?? []) out.push([n.id, b, "parallel"]);
    } else if (n.type === "human_approval" && n.on_reject) {
      out.push([n.id, n.on_reject, "rejected"]);
    } else if (n.type === "decision" && n.on_low_confidence?.route) {
      out.push([n.id, n.on_low_confidence.route, "low_confidence"]);
    }
    if (n.on_limit) out.push([n.id, n.on_limit, "limit"]);
    // A system step's "if it still fails, go to" (ADR-0079).
    if (n.type === "tool" && typeof n.on_error === "string" && n.on_error && n.on_error !== "fail" && n.on_error !== "continue") out.push([n.id, n.on_error, "error"]);
    // A "Wait for" step's "if nothing comes in time" (ADR-0094).
    if (n.type === "wait" && n.on_give_up) out.push([n.id, n.on_give_up, "give_up"]);
    // A for-each step goes to the first of its steps for each item (ADR-0104).
    const first = n.type === "foreach" ? n.steps?.[0] : undefined;
    if (first) out.push([n.id, first, "each"]);
  }
  return out;
}

export interface FlowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: { label: string; kind: string; max_iterations: number | null; entry: boolean };
}
export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  animated: boolean;
}
export interface FlowLayout {
  nodes: FlowNode[];
  edges: FlowEdge[];
  direction: "rtl" | "ltr";
}

/** Layered layout (BFS depth from entry); RTL mirrors the x axis so the flow reads right-to-left. */
export function toReactFlow(spec: AgentSpec, lang: "ar" | "en" = "en"): FlowLayout {
  const g = spec.spec.graph;
  const edges = specEdges(spec);
  const succ = new Map<string, string[]>();
  for (const [s, t] of edges) succ.set(s, [...(succ.get(s) ?? []), t]);
  const depth = new Map<string, number>([[g.entry, 0]]);
  const q = [g.entry];
  while (q.length) {
    const cur = q.shift() as string;
    for (const nxt of succ.get(cur) ?? []) {
      if (!depth.has(nxt)) {
        depth.set(nxt, (depth.get(cur) ?? 0) + 1);
        q.push(nxt);
      }
    }
  }
  const rows = new Map<number, number>();
  const sign = lang === "ar" ? -1 : 1;
  const maxDepth = Math.max(0, ...depth.values());
  const nodes: FlowNode[] = g.nodes.map((n) => {
    const d = depth.get(n.id) ?? maxDepth + 1;
    const row = rows.get(d) ?? 0;
    rows.set(d, row + 1);
    const label = n.label ? (lang === "ar" ? n.label.ar : n.label.en) : n.id;
    return {
      id: n.id,
      type: n.type,
      position: { x: sign * d * 240 || 0, y: row * 120 }, // `|| 0` avoids -0 for the entry column
      data: { label, kind: n.type, max_iterations: n.max_iterations ?? null, entry: n.id === g.entry },
    };
  });
  return {
    nodes,
    edges: edges.map(([s, t, w], i) => ({ id: `e${i}`, source: s, target: t, label: w ?? "", animated: w === "parallel" })),
    direction: lang === "ar" ? "rtl" : "ltr",
  };
}
